import { ComponentConfig, FieldLabel } from '@measured/puck';
import { MarginProps, marginFields } from '../../puck/reusable-props/margin';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import ArrowRight from '../../../assets/svg/ArrowRight';
import './../text-editor/style.scss';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import { Switch } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import { Tooltip } from '@mui/material';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import { CheckBoxComponent } from './checkBox';
import { verticalAligns } from '../../../page-builder/puck/reusable-props/options';
import Select from '../../../components/common/select';
import { getIsEdit } from '../../utils/constants';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import { useEffect, useState } from 'react';
import WithAccordion from '../customComponents/CustomAccordion';
import QuilHelper from '../../puck/reusable-props/quilHelper';

interface requiredForPaymentProps {
  isRequired: boolean;
  errorMessage?: string;
}

export type CheckBoxProps = {
  toggleOption: string;
  headerContent: string;
  acknowledgeContent: string;
  description: string;
  iconColor: string;
  showAcknowledgeDropdown: boolean;
  showHeader: boolean;
  showDropdown: boolean;
  border: IBorderValueProps;
  checkBoxVerticalAlign: string;
  color: string;
  defaultChecked: boolean;
  defaultCollapsed: boolean;
  showOnlyOnVip: boolean;
  background: {
    color: string;
    isInherit: boolean;
  };
  requiredForPayment: requiredForPaymentProps;
} & MarginProps &
  PaddingProps &
  showComponentProps;

export const CheckBox = ({
  useAppDispatch,
  useAppSelector,
}: ReduxDispatchProps & ReduxSelectorProps): ComponentConfig<CheckBoxProps> => {
  return {
    label: (
      <CustomTooltip title="TermsAndSub">
        <span>Checkbox</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,

      requiredForPayment: {
        type: 'custom',
        label: 'Required For Payment',
        render: ({ value, onChange }) => (
          <WithAccordion title="Required For Payment">
            <>
              <div className="flex gap-1 items-center justify-between">
                <CustomTooltip title="Required For Payment">
                  <span>Required Validation</span>
                </CustomTooltip>
                <Switch
                  checked={value?.isRequired}
                  onChange={e => {
                    onChange({
                      ...value,
                      isRequired: e.target.checked,
                    });
                  }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>

              {value?.isRequired && (
                <>
                  <h3>Error Message</h3>
                  <QuilHelper
                    value={value?.errorMessage || ''}
                    onChange={val => {
                      onChange({
                        ...value,
                        errorMessage: val,
                      });
                    }}
                    key={'errorMessage'}
                  />
                </>
              )}
            </>
          </WithAccordion>
        ),
      },
      showOnlyOnVip: {
        type: 'custom',
        label: 'Show Only On VIP',
        render: ({ value, onChange }) => (
          <div className="flex gap-2 items-center justify-between">
            <label>Show Only On VIP</label>
            <Switch
              checked={value}
              onChange={e => onChange(e.target.checked)}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </div>
        ),
      },
      toggleOption: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <div>
            <FieldLabel
              label={
                (
                  <CustomTooltip title="EnrollementStyle">
                    <span>Toggles Style</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }
              el="div"
            />
            <Select
              value={value}
              onChange={e => onChange(e)}
              options={[
                {
                  label: 'Checkbox',
                  value: 'checkbox',
                },
                {
                  label: 'Toggle',
                  value: 'toggle',
                },
              ]}
            />
          </div>
        ),
      },
      color: {
        type: 'custom',
        label: 'Checkbox Color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      defaultChecked: {
        type: 'custom',
        label: 'Default Open',
        render: ({ value, onChange }) => (
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm font-semibold">Default Checked</p>
            <div className="flex gap-0.5 items-center justify-between">
              <Tooltip
                title={
                  'To toggle the radio button, it is set to default checked when the webpage loads.'
                }
                placement="right-end">
                <InfoIcon fontSize="small" className="h-1 w-1" />
              </Tooltip>
              <Switch
                checked={value}
                onChange={e => onChange(e.target.checked)}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </div>
          </div>
        ),
      },
      ...marginFields,
      ...backgroungFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      showHeader: {
        type: 'radio',
        label: 'Show Header',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      headerContent: {
        ...QuillTextEditor('Header Content', true, 1).content,
      },
      acknowledgeContent: {
        ...QuillTextEditor('Content', true, 2).content,
      },
      showAcknowledgeDropdown: {
        type: 'radio',
        label: 'Show Dropdown Arrow',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      checkBoxVerticalAlign: {
        label: 'Vertical Align',
        type: 'select',
        options: verticalAligns,
      },
      showDropdown: {
        type: 'radio',
        label: 'Show Dropdown',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      iconColor: {
        type: 'custom',
        label: 'Icon Color',
        render: ({ value, onChange }) => (
          <ColorPicker
            label="Icon Color"
            value={value}
            onChange={onChange}
            tooltip="Icon Color"
          />
        ),
      },
      defaultCollapsed: {
        type: 'custom',
        label: 'Default Collapsed',
        render: ({ value, onChange }) => (
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm font-semibold">Default Expanded</p>
            <div className="flex gap-0.5 items-center justify-between">
              <Tooltip
                title={
                  'Toggling this will either collapse or expand the General Terms by default.'
                }
                placement="right-end">
                <InfoIcon fontSize="small" className="h-1 w-1" />
              </Tooltip>
              <Switch
                checked={value}
                onChange={e => onChange(e.target.checked)}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </div>
          </div>
        ),
      },
      description: {
        ...QuillTextEditor('Dropdown content', true, 3).content,
      },
    },
    defaultProps: {
      defaultChecked: false,
      showOnlyOnVip: false,
      defaultCollapsed: false,
      color: '#4f46e5',
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      checkBoxVerticalAlign: 'justify-start',
      showAcknowledgeDropdown: false,
      showHeader: false,
      showDropdown: false,
      description: '',
      iconColor: '#000000',
      showComponent: ['desktop', 'tablet', 'mobile'],
      toggleOption: 'checkbox',
      requiredForPayment: {
        isRequired: false,
        errorMessage:
          '<p style="line-height: 1;"><span style="color: rgb(232, 23, 23); font-size: 12px;">Please accept the terms to continue</span></p>',
      },
      ...borderProps,
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '10',
          mobile: '8',
          tablet: '10',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      headerContent:
        '<h3 style="line-height:1; style="font-size: 14px;"><span>Header Content</span></h3>',
      acknowledgeContent:
        '<p style="line-height:1.5;"><span style="font-size: 14px;">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna</span></p>',
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
    },
    render: ({
      margin,
      headerContent,
      acknowledgeContent,
      toggleOption,
      showComponent,
      checkBoxVerticalAlign,
      defaultChecked,
      border,
      padding,
      background,
      editMode,
      description,
      iconColor,
      showAcknowledgeDropdown,
      showHeader,
      showDropdown,
      showOnlyOnVip,
      color,
      defaultCollapsed,
      requiredForPayment,
    }) => {
      const { isRequired = false, errorMessage = '' } =
        requiredForPayment || {};
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const { paddingLeft, paddingRight } = getPaddingStyles(
        padding,
        currentDeviceType,
      );
      const [open, setOpen] = useState(false);
      const state = useAppSelector(state => state);
      const newDescription = getQuillEditorText(description || '', state);
      const isShowOnlyOnVip =
        useAppSelector(state => state.checkout.hasSubscription) ||
        !showOnlyOnVip;
      const isEditMode = getIsEdit();

      useEffect(() => {
        setOpen(defaultCollapsed);
      }, [defaultCollapsed]);
      return (
        <>
          {showComponent?.includes(currentDeviceType) &&
            (isShowOnlyOnVip || isEditMode) && (
              <div
                className={`${border?.borderStyle?.[currentDeviceType]} w-full text-editor-content ${showDropdown ? ' grid grid-cols-[auto_1fr] ' : ''}`}
                style={{
                  ...marginStyles,
                  backgroundColor: background?.isInherit
                    ? 'inherit'
                    : background?.color,
                  ...getBorderProps(border, currentDeviceType),
                  ...getPaddingStyles(padding, currentDeviceType),
                }}>
                {showDropdown && (
                  <div
                    className={`flex ${checkBoxVerticalAlign} flex-col py-2`}>
                    <ArrowRight
                      onClick={() => setOpen(!open)}
                      fill={iconColor}
                      className={`${open ? 'transform rotate-90' : ''}  mx-2`}
                    />
                  </div>
                )}
                <CheckBoxComponent
                  color={color}
                  showAcknowledgeDropdown={showAcknowledgeDropdown}
                  showHeader={showHeader}
                  headerContent={headerContent}
                  acknowledgeContent={acknowledgeContent}
                  toggleOption={toggleOption}
                  defaultChecked={defaultChecked}
                  useAppDispatch={useAppDispatch}
                  useAppSelector={useAppSelector}
                  checkBoxVerticalAlign={checkBoxVerticalAlign}
                  checkBoxData={{
                    isRequired,
                    errorMessage: errorMessage || '',
                  }}
                />
                <div></div>
                {open && showDropdown && (
                  <div
                    style={{ paddingLeft, paddingRight, lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{ __html: newDescription }}></div>
                )}
              </div>
            )}
        </>
      );
    },
  };
};
